/*
Copyright (c)  CYBERKOALA LLC 2025.
Proprietary License.
Redistribution and use in source and binary forms, with or without
modification, are restricted.
*/
import {
  CaptionContext,
  CaptionEntry,
  CaptionFeedItem,
} from '@/context/caption-context/caption-context.tsx';
import React, { ReactNode } from 'react';

interface CaptionProviderProps {
  children: ReactNode;
  /**
   * Pass current call/channel id.
   * When it changes, captions and caption visibility are reset.
   *
   * Example:
   * <CaptionProvider callId={activeCall?.chatId}>
   */
  callId?: string | null;
}

export function CaptionProvider({ children, callId }: CaptionProviderProps) {
  const [captionLine, setCaptionLine] = React.useState('');
  const [feed, setFeed] = React.useState<CaptionFeedItem[]>([]);
  const [captionsEnabled, setCaptionsEnabledState] = React.useState(false);

  const MAX_FEED_LENGTH = 10;
  const MAX_AGE_MS = 30_000; // 30 секунд

  const trimCaption = (text: string, maxLen = 400) => {
    if (text.length <= maxLen) return text;
    return text.slice(0, maxLen) + '…';
  };

  const clearCaptions = React.useCallback(() => {
    setCaptionLine('');
    setFeed([]);
  }, []);

  const setCaptionsEnabled = React.useCallback((enabled: boolean) => {
    setCaptionsEnabledState(enabled);
  }, []);

  const toggleCaptions = React.useCallback(() => {
    setCaptionsEnabledState((prev) => !prev);
  }, []);

  const addCaption = React.useCallback(
    (entry: CaptionEntry) => {
      // If captions are turned off, do not accumulate caption data.
      if (!captionsEnabled) return;

      setCaptionLine((prev) => `${prev ? prev + ' ' : ''}${entry.text}`);
      const trimmed = trimCaption(entry.text);

      setFeed((prev) => {
        const next = [
          ...prev,
          {
            id: crypto.randomUUID(),
            speaker: entry.speaker,
            text: trimmed,
            timestamp: entry.ts,
          },
        ];
        return next.length > MAX_FEED_LENGTH ? next.slice(-MAX_FEED_LENGTH) : next;
      });
    },
    [captionsEnabled]
  );

  /**
   * If captions are disabled, clear current visible captions.
   */
  React.useEffect(() => {
    if (!captionsEnabled) {
      clearCaptions();
    }
  }, [captionsEnabled, clearCaptions]);

  /**
   * Reset captions on next call join.
   * If parent passes callId={chatId}, then changing channel/call
   * will re-enable captions and clear old feed.
   */
  React.useEffect(() => {
    // FIX: Было false, из-за чего субтитры выключались при инициализации callId.
    // Теперь они включаются автоматически при новом звонке, как и сказано в комментарии и на кнопке.
    setCaptionsEnabledState(false);
    clearCaptions();
  }, [callId, clearCaptions]);

  /**
   * Auto-clear captions after 30 seconds.
   */
  React.useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setFeed((prev) => {
        const filtered = prev.filter((item) => now - item.timestamp < MAX_AGE_MS);
        if (filtered.length === prev.length) {
          return prev;
        }
        return filtered;
      });
    }, 1_000);

    return () => clearInterval(interval);
  }, []);

  return (
    <CaptionContext.Provider
      value={{
        feed,
        captionLine,
        captionsEnabled,
        addCaption,
        clearCaptions,
        toggleCaptions,
        setCaptionsEnabled,
      }}
    >
      {children}
    </CaptionContext.Provider>
  );
}
